import { useState } from "react";
import * as Sentry from "@sentry/react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { RoleRedirect } from "@/components/auth/RoleRedirect";
import { ROUTES } from "@/routes";
import Auth from "./pages/Auth";
import ResetPassword from "./pages/ResetPassword";
import BillingAuth from "./pages/billing/BillingAuth";
import BillingDashboard from "./pages/billing/BillingDashboard";
import ClaimsManagement from "./pages/billing/ClaimsManagement";
import PayerManagement from "./pages/billing/PayerManagement";
import ClaimsReports from "./pages/billing/ClaimsReports";
import ARAgingPage from "./pages/billing/ARAgingPage";
import DenialsManagement from "./pages/billing/DenialsManagement";
import BillingSettings from "./pages/billing/BillingSettings";
import BillingHelp from "./pages/billing/BillingHelp";
import ComplianceDashboard from "./pages/billing/ComplianceDashboard";
import ProviderDatabase from "./pages/billing/ProviderDatabase";
import ProviderMemberAssignments from "./pages/billing/ProviderMemberAssignments";
import ProviderDashboard from "./pages/provider/ProviderDashboard";
import CaseManagement from "./pages/provider/CaseManagement";
import PatientDetail from "./pages/provider/PatientDetail";
import MyPatients from "./pages/provider/MyPatients";
import ProviderSchedule from "./pages/provider/ProviderSchedule";
import ProviderTelehealth from "./pages/provider/ProviderTelehealth";
import SessionDocumentation from "./pages/provider/SessionDocumentation";
import RemoteMonitoring from "./pages/provider/RemoteMonitoring";
import CarePlans from "./pages/provider/CarePlans";
import PrenatalCareCoordination from "./pages/provider/PrenatalCareCoordination";
import ProviderIntakePage from "./pages/provider/ProviderIntakePage";
import ProviderOnboarding from "./pages/provider/ProviderOnboarding";
import HealthSearchAnalytics from "./pages/provider/HealthSearchAnalytics";
import ProviderProfile from "./pages/provider/ProviderProfile";
import MemberDashboard from "./pages/member/MemberDashboard";
import MemberOnboarding from "./pages/member/MemberOnboarding";
import ProviderPrenatalIntakePage from "./pages/provider/ProviderPrenatalIntakePage";
import MemberIntakePage from "./pages/member/IntakePage";
import MemberHealth from "./pages/member/MemberHealth";
import MemberScheduling from "./pages/member/MemberScheduling";
import MemberAppointments from "./pages/member/MemberAppointments";
import MemberNotifications from "./pages/member/MemberNotifications";
import MemberDevices from "./pages/member/MemberDevices";
import HealthFaqSearch from "./pages/member/HealthFaqSearch";
import MemberProfile from "./pages/member/MemberProfile";
import TelehealthSession from "./pages/telehealth/TelehealthSession";
import WaitingRoom from "./pages/telehealth/WaitingRoom";
import NotFound from "./pages/NotFound";

const App = () => {
  const [queryClient] = useState(() => new QueryClient());

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
          <SentryRoutes>
            {/* Public routes */}
            <Route path={ROUTES.AUTH} element={<Auth />} />
            <Route path={ROUTES.RESET_PASSWORD} element={<ResetPassword />} />
            <Route path={ROUTES.BILLING_AUTH} element={<BillingAuth />} />
            
            {/* Role-based redirect from root */}
            <Route path={ROUTES.ROOT} element={<RoleRedirect />} />
            
            {/* Billing routes */}
            <Route
              path={ROUTES.BILLING}
              element={
                <ProtectedRoute allowedRoles={["billing"]}>
                  <BillingDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.BILLING_CLAIMS}
              element={
                <ProtectedRoute allowedRoles={["billing"]}>
                  <ClaimsManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.BILLING_PAYERS}
              element={
                <ProtectedRoute allowedRoles={["billing"]}>
                  <PayerManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.BILLING_REPORTS}
              element={
                <ProtectedRoute allowedRoles={["billing"]}>
                  <ClaimsReports />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.BILLING_AGING}
              element={
                <ProtectedRoute allowedRoles={["billing"]}>
                  <ARAgingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.BILLING_DENIALS}
              element={
                <ProtectedRoute allowedRoles={["billing"]}>
                  <DenialsManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.BILLING_SETTINGS}
              element={
                <ProtectedRoute allowedRoles={["billing"]}>
                  <BillingSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.BILLING_HELP}
              element={
                <ProtectedRoute allowedRoles={["billing"]}>
                  <BillingHelp />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.BILLING_COMPLIANCE}
              element={
                <ProtectedRoute allowedRoles={["billing"]}>
                  <ComplianceDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.BILLING_PROVIDERS}
              element={
                <ProtectedRoute allowedRoles={["billing"]}>
                  <ProviderDatabase />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.BILLING_ASSIGNMENTS}
              element={
                <ProtectedRoute allowedRoles={["billing"]}>
                  <ProviderMemberAssignments />
                </ProtectedRoute>
              }
            />
            
            {/* Provider onboarding */}
            <Route
              path={ROUTES.PROVIDER_ONBOARDING}
              element={
                <ProtectedRoute allowedRoles={["provider"]}>
                  <ProviderOnboarding />
                </ProtectedRoute>
              }
            />

            {/* Provider routes */}
            <Route
              path={ROUTES.PROVIDER}
              element={
                <ProtectedRoute allowedRoles={["provider"]}>
                  <ProviderDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.PROVIDER_PATIENTS}
              element={
                <ProtectedRoute allowedRoles={["provider"]}>
                  <MyPatients />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.PROVIDER_CASES}
              element={
                <ProtectedRoute allowedRoles={["provider"]}>
                  <CaseManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.PROVIDER_PATIENT}
              element={
                <ProtectedRoute allowedRoles={["provider"]}>
                  <PatientDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.PROVIDER_SCHEDULE}
              element={
                <ProtectedRoute allowedRoles={["provider"]}>
                  <ProviderSchedule />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.PROVIDER_TELEHEALTH}
              element={
                <ProtectedRoute allowedRoles={["provider"]}>
                  <ProviderTelehealth />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.PROVIDER_SESSION_NOTES}
              element={
                <ProtectedRoute allowedRoles={["provider"]}>
                  <SessionDocumentation />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.PROVIDER_MONITORING}
              element={
                <ProtectedRoute allowedRoles={["provider"]}>
                  <RemoteMonitoring />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.PROVIDER_CARE_PLANS}
              element={
                <ProtectedRoute allowedRoles={["provider"]}>
                  <CarePlans />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.PROVIDER_PNCC}
              element={
                <ProtectedRoute allowedRoles={["provider"]}>
                  <PrenatalCareCoordination />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.PROVIDER_INTAKE}
              element={
                <ProtectedRoute allowedRoles={["provider"]}>
                  <ProviderIntakePage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.PROVIDER_PRENATAL_INTAKE}
              element={
                <ProtectedRoute allowedRoles={["provider"]}>
                  <ProviderPrenatalIntakePage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.PROVIDER_HEALTH_SEARCH_ANALYTICS}
              element={
                <ProtectedRoute allowedRoles={["provider"]}>
                  <HealthSearchAnalytics />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.PROVIDER_PROFILE}
              element={
                <ProtectedRoute allowedRoles={["provider"]}>
                  <ProviderProfile />
                </ProtectedRoute>
              }
            />
            
            {/* Member onboarding */}
            <Route
              path={ROUTES.MEMBER_ONBOARDING}
              element={
                <ProtectedRoute allowedRoles={["member"]}>
                  <MemberOnboarding />
                </ProtectedRoute>
              }
            />

            {/* Member routes */}
            <Route
              path={ROUTES.MEMBER}
              element={
                <ProtectedRoute allowedRoles={["member"]}>
                  <MemberDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.MEMBER_PROFILE}
              element={
                <ProtectedRoute allowedRoles={["member"]}>
                  <MemberProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.MEMBER_INTAKE}
              element={
                <ProtectedRoute allowedRoles={["member"]}>
                  <MemberIntakePage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.MEMBER_HEALTH}
              element={
                <ProtectedRoute allowedRoles={["member"]}>
                  <MemberHealth />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.MEMBER_SCHEDULE}
              element={
                <ProtectedRoute allowedRoles={["member"]}>
                  <MemberScheduling />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.MEMBER_APPOINTMENTS}
              element={
                <ProtectedRoute allowedRoles={["member"]}>
                  <MemberAppointments />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.MEMBER_NOTIFICATIONS}
              element={
                <ProtectedRoute allowedRoles={["member"]}>
                  <MemberNotifications />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.MEMBER_DEVICES}
              element={
                <ProtectedRoute allowedRoles={["member"]}>
                  <MemberDevices />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.MEMBER_HEALTH_FAQ}
              element={
                <ProtectedRoute allowedRoles={["member"]}>
                  <HealthFaqSearch />
                </ProtectedRoute>
              }
            />

            {/* Telehealth routes - accessible by both roles */}
            <Route
              path={ROUTES.TELEHEALTH_WAITING}
              element={
                <ProtectedRoute allowedRoles={["member"]}>
                  <WaitingRoom />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.TELEHEALTH_SESSION}
              element={
                <ProtectedRoute allowedRoles={["member", "provider"]}>
                  <TelehealthSession />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.TELEHEALTH_SESSION_ID}
              element={
                <ProtectedRoute allowedRoles={["member", "provider"]}>
                  <TelehealthSession />
                </ProtectedRoute>
              }
            />
            
            {/* Catch-all */}
            <Route path="*" element={<NotFound />} />
          </SentryRoutes>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
  );
};

export default App;
